import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, X, Upload, Globe, GripVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Product, Category } from './ProductCard';
import { compressProductImage, compressProductGalleryImage } from '../lib/imageCompression';

export interface LeasingCategoryInfo {
  id: string;
  name: string;
  color: string;
}

interface ProductEditFormProps {
  product: Product;
  categories: Category[];
  onSave: (product: Product, leasingCategoryIds?: string[]) => Promise<void>;
  onCancel: () => void;
  themeColor?: string;
  leasingCategories?: LeasingCategoryInfo[];
  leasingCategoryIds?: string[];
}

export default function ProductEditForm({
  product,
  categories,
  onSave,
  onCancel,
  themeColor = "[#EE1C23]",
  leasingCategories = [],
  leasingCategoryIds = []
}: ProductEditFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(() => {
    // Ensure name and description are proper objects
    const ensureObject = (field: any) => {
      if (typeof field === 'string') {
        return { cs: field, en: '', de: '', sk: '' };
      }
      return field || { cs: '', en: '', de: '', sk: '' };
    };

    return {
      ...product,
      name: ensureObject(product.name),
      description: ensureObject(product.description),
      categories: product.categories || []
    };
  });

  const [imagePreview, setImagePreview] = useState(product.image);
  const [additionalImages, setAdditionalImages] = useState<string[]>(product.images || []);
  const [showTranslations, setShowTranslations] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLeasingCats, setSelectedLeasingCats] = useState<string[]>(leasingCategoryIds);
  
  // Define focus class for inputs
  const focusClass = themeColor === "blue-500" ? "focus:border-blue-500" : 
                     themeColor === "green-500" ? "focus:border-green-500" :
                     themeColor === "pink-500" ? "focus:border-pink-500" :
                     "focus:border-[#EE1C23]";
  
  const handleAdditionalImageUpload = async (files: FileList) => {
    const compressedImages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const compressedImage = await compressProductGalleryImage(files[i]);
        compressedImages.push(compressedImage);
      } catch (error) {
        console.error('Error compressing image:', error);
        alert(`Chyba při kompresi obrázku ${files[i].name}`);
      }
    }

    if (compressedImages.length > 0) {
      setAdditionalImages(prev => [...prev, ...compressedImages]);
    }
  };

  const handleManualUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      handleChange('manual', result);
    };
    reader.readAsDataURL(file);
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...additionalImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);
    setAdditionalImages(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(
        {
          ...formData,
          images: additionalImages.length > 0 ? additionalImages : [formData.image]
        },
        selectedLeasingCats
      );
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: string, value: any, lang?: string) => {
    if (lang) {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field as keyof Product] as any,
          [lang]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const compressedImage = await compressProductImage(file);
      handleChange('image', compressedImage);
      setImagePreview(compressedImage);
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Chyba při kompresi obrázku');
    }
  };

  const gradientClass = themeColor === "blue-500" ? "from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500" :
                       themeColor === "green-500" ? "from-green-500 to-green-600 hover:from-green-400 hover:to-green-500" :
                       themeColor === "pink-500" ? "from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500" :
                       "from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23]";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-gray-300">Název</label>
          <button
            type="button"
            onClick={() => setShowTranslations(!showTranslations)}
            className="flex items-center space-x-1 text-gray-400 hover:text-gray-300 text-sm"
          >
            <Globe className="w-4 h-4" />
            <span>{showTranslations ? 'Skrýt překlady' : 'Přidat překlady'}</span>
          </button>
        </div>
        
        <input
          type="text"
          value={formData.name.cs}
          onChange={(e) => handleChange('name', e.target.value, 'cs')}
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors`}
          placeholder="Název produktu"
          required
        />
        
        {showTranslations && (
          <div className="mt-3 space-y-2">
            <input
              type="text"
              value={formData.name.en}
              onChange={(e) => handleChange('name', e.target.value, 'en')}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors`}
              placeholder="English name"
            />
            <input
              type="text"
              value={formData.name.de}
              onChange={(e) => handleChange('name', e.target.value, 'de')}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors`}
              placeholder="Deutscher Name"
            />
            <input
              type="text"
              value={formData.name.sk}
              onChange={(e) => handleChange('name', e.target.value, 'sk')}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors`}
              placeholder="Slovenský názov"
            />
          </div>
        )}
      </div>

      {/* Description Section */}
      <div>
        <label className="block text-gray-300 mb-2">Popis</label>
        <textarea
          value={formData.description.cs}
          onChange={(e) => handleChange('description', e.target.value, 'cs')}
          rows={3}
          className={`w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors resize-none`}
          placeholder="Popis produktu"
          required
        />
        
        {showTranslations && (
          <div className="mt-3 space-y-2">
            <textarea
              value={formData.description.en}
              onChange={(e) => handleChange('description', e.target.value, 'en')}
              rows={2}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors resize-none`}
              placeholder="English description"
            />
            <textarea
              value={formData.description.de}
              onChange={(e) => handleChange('description', e.target.value, 'de')}
              rows={2}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors resize-none`}
              placeholder="Deutsche Beschreibung"
            />
            <textarea
              value={formData.description.sk}
              onChange={(e) => handleChange('description', e.target.value, 'sk')}
              rows={2}
              className={`w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors resize-none`}
              placeholder="Slovenský popis"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-gray-300 mb-2">Cena (Kč)</label>
          <input
            type="number"
            value={formData.price}
            onChange={(e) => {
              const czk = parseInt(e.target.value) || 0;
              handleChange('price', czk);
            }}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            placeholder="Cena v Kč"
            required
          />
        </div>
        <div>
          <label className="block text-gray-300 mb-2">Sleva (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            value={formData.discount || 0}
            onChange={(e) => handleChange('discount', parseInt(e.target.value) || 0)}
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            placeholder="0"
          />
          <p className="text-gray-500 text-xs mt-1">
            {formData.discount ? `Sleva: ${formData.discount}%` : 'Bez slevy'}
          </p>
        </div>
      </div>

      {/* VAT (DPH) toggle — hidden for unlimited section */}
      {formData.category !== 'unlimited' && (
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.vatIncluded || false}
            onChange={(e) => handleChange('vatIncluded', e.target.checked)}
            className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
              themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
              themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
              themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
              themeColor === "amber-500" ? "text-amber-500 focus:ring-amber-500" :
              "text-[#EE1C23] focus:ring-[#EE1C23]"
            }`}
          />
          <span className="text-gray-300">{t('shop.priceWithVat')}</span>
          <span className="text-gray-500 text-xs">
            {t('shop.priceWithoutVatHint')}
          </span>
        </label>
      )}

      {/* Main Image */}
      <div>
        <label className="block text-gray-300 mb-2">Hlavní obrázek</label>
        <div className="space-y-3">
          <label className="bg-blue-600 hover:bg-blue-500 text-white py-3 px-4 rounded-lg cursor-pointer flex items-center justify-center transition-colors">
            <Upload className="w-5 h-5 mr-2" />
            {imagePreview ? 'Změnit obrázek' : 'Nahrát obrázek'}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
              }}
              className="hidden"
            />
          </label>
          {imagePreview && (
            <div className="relative w-32 h-32">
              <img
                src={imagePreview}
                alt="Náhled"
                className="w-full h-full object-cover rounded border border-gray-600"
              />
              <button
                type="button"
                onClick={() => {
                  handleChange('image', '');
                  setImagePreview('');
                }}
                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-500 z-10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Additional Images */}
      <div>
        <label className="block text-gray-300 mb-2">Další obrázky</label>
        <div className="space-y-3">
          <label className="bg-blue-600 hover:bg-blue-500 text-white py-3 px-4 rounded-lg cursor-pointer flex items-center justify-center transition-colors">
            <Upload className="w-5 h-5 mr-2" />
            Přidat obrázky
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  handleAdditionalImageUpload(files);
                }
                e.target.value = '';
              }}
              className="hidden"
            />
          </label>

          {additionalImages.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {additionalImages.map((img, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`relative cursor-move group ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <div className="absolute top-1 left-1 bg-gray-900/80 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                  </div>
                  <img
                    src={img}
                    alt={`Obrázek ${index + 1}`}
                    className="w-full h-16 object-cover rounded border border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => removeAdditionalImage(index)}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-500 z-10"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manual */}
      <div>
        <label className="block text-gray-300 mb-2">Manuál (PDF)</label>
        <div className="space-y-3">
          <label className="bg-purple-600 hover:bg-purple-500 text-white py-3 px-4 rounded-lg cursor-pointer flex items-center justify-center transition-colors">
            <Upload className="w-5 h-5 mr-2" />
            {formData.manual ? 'Změnit manuál' : 'Nahrát manuál (PDF)'}
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleManualUpload(file);
              }}
              className="hidden"
            />
          </label>
          {formData.manual && (
            <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
              <span className="text-gray-300 text-sm">Manuál nahrán ✓</span>
              <button
                type="button"
                onClick={() => handleChange('manual', '')}
                className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Video Tutorial */}
      <div>
        <label className="block text-gray-300 mb-2">Video tutoriál</label>
        <div className="space-y-3">
          <input
            type="url"
            value={formData.videoTutorial || ''}
            onChange={(e) => handleChange('videoTutorial', e.target.value)}
            placeholder="YouTube embed URL (např. https://www.youtube.com/embed/VIDEO_ID)"
            className={`w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors`}
          />
          <a
            href={formData.videoTutorial || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors ${
              !formData.videoTutorial ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <span>🎥</span>
            <span>Přehrát video</span>
          </a>
          <p className="text-gray-500 text-xs">
            Použijte YouTube embed URL ve formátu: https://www.youtube.com/embed/VIDEO_ID
          </p>
        </div>
      </div>

      {/* Categories */}
      <div>
        <label className="block text-gray-300 mb-2">Kategorie</label>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <label key={category.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.categories?.includes(category.id) || false}
                onChange={(e) => {
                  const currentCategories = formData.categories || [];
                  if (e.target.checked) {
                    handleChange('categories', [...currentCategories, category.id]);
                  } else {
                    handleChange('categories', currentCategories.filter(c => c !== category.id));
                  }
                }}
                className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
                  themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
                  themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
                  themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
                  "text-[#EE1C23] focus:ring-[#EE1C23]"
                }`}
              />
              <span 
                className="px-2 py-1 rounded-full text-xs font-bold text-white"
                style={{backgroundColor: category.color}}
              >
                {category.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Leasing Categories */}
      {leasingCategories.length > 0 && (
        <div className="border border-red-500/30 rounded-lg p-4 space-y-3 bg-gray-900/50">
          <label className="block text-gray-300 font-semibold text-sm">Leasingové kategorie</label>
          <div className="flex flex-wrap gap-2">
            {leasingCategories.map((lc) => {
  const checked = selectedLeasingCats.includes(lc.id);
  return (
    <label key={lc.id} className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedLeasingCats(prev => [...prev, lc.id]);
          } else {
            setSelectedLeasingCats(prev => prev.filter(id => id !== lc.id));
          }
        }}
        className="w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 text-[#EE1C23] focus:ring-[#EE1C23]"
      />
      <span
        className="px-2 py-1 rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: lc.color }}
      >
        {lc.name}
      </span>
    </label>
  );
})}
          </div>
        </div>
      )}

      {/* Product Options */}
      <div className="grid grid-cols-3 gap-3">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.inStock}
            onChange={(e) => handleChange('inStock', e.target.checked)}
            className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
              themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
              themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
              themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
              "text-[#EE1C23] focus:ring-[#EE1C23]"
            }`}
          />
          <span className="text-gray-300">Skladem</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.isRecommended}
            onChange={(e) => handleChange('isRecommended', e.target.checked)}
            className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
              themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
              themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
              themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
              "text-[#EE1C23] focus:ring-[#EE1C23]"
            }`}
          />
          <span className="text-gray-300">Doporučené</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.isSoldOut}
            onChange={(e) => handleChange('isSoldOut', e.target.checked)}
            className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
              themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
              themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
              themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
              "text-[#EE1C23] focus:ring-[#EE1C23]"
            }`}
          />
          <span className="text-gray-300">Vyprodáno</span>
        </label>
      </div>

      {/* Rental / Operational Leasing */}
      <div className="border border-gray-700 rounded-lg p-4 space-y-4 bg-gray-900/50">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.isRental || false}
            onChange={(e) => handleChange('isRental', e.target.checked)}
            className={`w-4 h-4 bg-gray-800 border-gray-600 rounded focus:ring-2 ${
              themeColor === "blue-500" ? "text-blue-500 focus:ring-blue-500" :
              themeColor === "green-500" ? "text-green-500 focus:ring-green-500" :
              themeColor === "pink-500" ? "text-pink-500 focus:ring-pink-500" :
              "text-[#EE1C23] focus:ring-[#EE1C23]"
            }`}
          />
          <span className="text-gray-300 font-semibold">{t('shop.rentalAvailable')}</span>
        </label>
        {formData.isRental && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-6">
            <div>
              <label className="block text-gray-300 mb-1 text-sm">{t('shop.rentalMonthlyFlat')}</label>
              <input
                type="number"
                min="0"
                value={formData.rentalPriceMonthly || 0}
                onChange={(e) => handleChange('rentalPriceMonthly', parseInt(e.target.value) || 0)}
                className={`w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-1 text-sm">{t('shop.rentalInsurance')}</label>
              <input
                type="number"
                min="0"
                value={formData.rentalInsuranceMonthly || 0}
                onChange={(e) => handleChange('rentalInsuranceMonthly', parseInt(e.target.value) || 0)}
                className={`w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-1 text-sm">{t('shop.rentalHourlyRate')}</label>
              <input
                type="number"
                min="0"
                value={formData.rentalHourlyRate || 0}
                onChange={(e) => handleChange('rentalHourlyRate', parseInt(e.target.value) || 0)}
                className={`w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white ${focusClass} focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                placeholder="0"
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Zrušit
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className={`flex-1 bg-gradient-to-r ${gradientClass} text-white py-3 rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
        >
          {isSaving ? (
            <>
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Ukládání změn...</span>
            </>
          ) : (
            <span>Uložit změny</span>
          )}
        </button>
      </div>
    </form>
  );
}